import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { Card, Checkbox, FormLayout, Layout, TextField } from "@shopify/polaris";

export default function ServiceWindowSettings({ onSave, shop_config, saving }) {
  const [form, setForm] = useState({
    pickup_lead_time: shop_config.pickup_lead_time ?? shop_config.lead_time ?? 0,
    delivery_lead_time: shop_config.delivery_lead_time ?? shop_config.lead_time ?? 0,
    window_count: shop_config.window_count,
    sameday_limit: shop_config.sameday_limit,
    allow_weekend_sameday: shop_config.allow_weekend_sameday || false,
    picking_fee_enabled: shop_config.picking_fee_enabled || false,
    picking_fee_cents: shop_config.picking_fee_cents || 0,
    picking_fee_methods: shop_config.picking_fee_methods || [],
    cart_minimum_cents: shop_config.cart_minimum_cents || 0,
  });

  useEffect(() => {
    setForm({
      pickup_lead_time: shop_config.pickup_lead_time ?? shop_config.lead_time ?? 0,
      delivery_lead_time: shop_config.delivery_lead_time ?? shop_config.lead_time ?? 0,
      window_count: shop_config.window_count,
      sameday_limit: shop_config.sameday_limit,
      allow_weekend_sameday: shop_config.allow_weekend_sameday || false,
      picking_fee_enabled: shop_config.picking_fee_enabled || false,
      picking_fee_cents: shop_config.picking_fee_cents || 0,
      picking_fee_methods: shop_config.picking_fee_methods || [],
      cart_minimum_cents: shop_config.cart_minimum_cents || 0,
    });
  }, [shop_config]);

  const handleChange = (key, val) => {
    setForm((form) => ({
      ...form,
      [key]: val,
    }));
  };

  const toggleMethod = (method) => {
    setForm((current) => {
      const methods = current.picking_fee_methods || [];
      const nextMethods = methods.includes(method)
        ? methods.filter((m) => m !== method)
        : [...methods, method];
      return {
        ...current,
        picking_fee_methods: nextMethods,
      };
    });
  };

  const handleSave = (_event) => {
    const fallbackPickup = shop_config.pickup_lead_time ?? shop_config.lead_time ?? 0;
    const fallbackDelivery = shop_config.delivery_lead_time ?? shop_config.lead_time ?? 0;

    onSave({
      ...form,
      pickup_lead_time: Number.isFinite(form.pickup_lead_time)
        ? form.pickup_lead_time
        : fallbackPickup,
      delivery_lead_time: Number.isFinite(form.delivery_lead_time)
        ? form.delivery_lead_time
        : fallbackDelivery,
    });
  };

  const pickingFeeValue = Number.isFinite(form.picking_fee_cents)
    ? (form.picking_fee_cents / 100).toFixed(2)
    : "0.00";

  const cartMinimumValue = Number.isFinite(form.cart_minimum_cents)
    ? (form.cart_minimum_cents / 100).toFixed(2)
    : "0.00";

  const setPickingFeeCents = (value) => {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
      handleChange("picking_fee_cents", 0);
      return;
    }
    handleChange("picking_fee_cents", Math.max(0, Math.round(parsed * 100)));
  };

  const setCartMinimumCents = (value) => {
    const parsed = parseFloat(value);
    if (Number.isNaN(parsed)) {
      handleChange("cart_minimum_cents", 0);
      return;
    }
    handleChange("cart_minimum_cents", Math.max(0, Math.round(parsed * 100)));
  };

  const isMethodChecked = (method) =>
    (form.picking_fee_methods || []).includes(method);

  return (
    <Layout.AnnotatedSection
      title="Service Window Settings"
      description="Customize pickup and delivery window settings"
    >
      <Card
        sectioned
        primaryFooterAction={{
          content: "Save",
          loading: saving,
          onAction: handleSave,
          disabled: saving,
        }}
      >
        <FormLayout>
          <TextField
            label="Cart minimum order amount ($)"
            type="number"
            min={0}
            step="0.01"
            value={cartMinimumValue}
            onChange={setCartMinimumCents}
            helpText="Orders below this amount will not be able to check out. Set to 0.00 to disable."
          />

          <Checkbox
            label="Enable picking & handling fee"
            checked={form.picking_fee_enabled}
            onChange={(checked) =>
              setForm((current) => ({
                ...current,
                picking_fee_enabled: checked,
                picking_fee_methods: checked
                  ? current.picking_fee_methods || []
                  : [],
              }))
            }
          />

          <TextField
            label="Picking & handling fee ($)"
            type="number"
            min={0}
            step="0.01"
            value={pickingFeeValue}
            onChange={setPickingFeeCents}
            disabled={!form.picking_fee_enabled}
            helpText="Fee amount is stored in cents and applied when enabled."
          />

          <FormLayout.Group condensed>
            <Checkbox
              label="Apply to pickup"
              checked={isMethodChecked("pickup")}
              onChange={() => toggleMethod("pickup")}
              disabled={!form.picking_fee_enabled}
            />
            <Checkbox
              label="Apply to delivery"
              checked={isMethodChecked("delivery")}
              onChange={() => toggleMethod("delivery")}
              disabled={!form.picking_fee_enabled}
            />
            <Checkbox
              label="Apply to shipping"
              checked={isMethodChecked("shipping")}
              onChange={() => toggleMethod("shipping")}
              disabled={!form.picking_fee_enabled}
            />
          </FormLayout.Group>

          {/* <TextField
            label="Pickup Fee ($)"
            type="number"
            min={0}
            value={`${form.pickup_fee / 100.0}`}
            onChange={(val) => handleChange('pickup_fee', (+val * 100))}
          /> */}

          <FormLayout.Group>
            <TextField
              label="Pickup lead time (hours)"
              type="number"
              min={0}
              value={`${form.pickup_lead_time ?? ""}`}
              onChange={(val) => handleChange("pickup_lead_time", +val)}
            />
            <TextField
              label="Delivery lead time (hours)"
              type="number"
              min={0}
              value={`${form.delivery_lead_time ?? ""}`}
              onChange={(val) => handleChange("delivery_lead_time", +val)}
            />
          </FormLayout.Group>

          <TextField
            label="Maximum number of delivery windows to show"
            type="number"
            min={1}
            max={24}
            value={`${form.window_count}`}
            onChange={(val) => handleChange("window_count", +val)}
          />

          <TextField
            label="Same day order limit (Optional)"
            type="number"
            min={0}
            value={`${form.sameday_limit || 0}`}
            helpText="If you support same day orders, you can set a limit so that same day windows are not available if they already have this number of orders scheduled."
            onChange={(val) => handleChange("sameday_limit", +val)}
          />

          <Checkbox
            label="Allow same-day windows on weekends"
            checked={form.allow_weekend_sameday}
            onChange={(checked) =>
              handleChange("allow_weekend_sameday", checked)
            }
            helpText="When off, same-day windows are hidden on Saturdays and Sundays (except Trexity). Same-day cutoffs and lead times still apply."
          />
        </FormLayout>
      </Card>
    </Layout.AnnotatedSection>
  );
}

ServiceWindowSettings.propTypes = {
  onSave: PropTypes.func,
  shop_config: PropTypes.object,
  saving: PropTypes.bool,
};
